import * as React from 'react';

import { useShouldCellBeRegistered } from 'af-root/hooks/useShouldCellBeRegistered';

type EditModeCellWrapperProps = {
	name: string;
	readonly?: boolean;
	children: (isEditing: boolean, exitEditMode: () => void) => React.ReactNode;
};

/**
 * Wrapper around renderers that adds editingCellName functionality
 * Used for large field arrays for performance reasons
 * It determins if cell was/is in edit mode so far, because depending on that, we render different components in renderers
 * @param props
 * @returns wrapper around passed component that offers info if cell was/is edited and should it be registered in RHF
 */
function EditModeCellWrapper(props: EditModeCellWrapperProps) {

	const { name, children, readonly } = props;

	const { shouldBeRegistered, exitEditMode, reset } = useShouldCellBeRegistered(name);

	const wrapperRef = React.useRef<HTMLDivElement>(null);

	React.useEffect(() => {
		if (readonly) {
			reset();
		}
	}, [readonly, reset]);

	React.useEffect(() => {
		if (!shouldBeRegistered) {
			return;
		}

		const onMouseDownCapture = (e: MouseEvent) => {
			const target = e.target as HTMLElement | null;
			if (!target) {
				return;
			}

			// click inside this cell -> ignore
			if (wrapperRef.current?.contains(target)) {
				return;
			}

			// click inside dropdown menu
			// keep edit mode so selecting items works
			if (target.closest('.dropdown-menu')) {
				return;
			}

			exitEditMode();
		};

		document.addEventListener('mousedown', onMouseDownCapture, true);
		return () => document.removeEventListener('mousedown', onMouseDownCapture, true);
	}, [shouldBeRegistered, exitEditMode]);

	return (
		<>
			{children(shouldBeRegistered, exitEditMode)}
		</>
	);
}

export default EditModeCellWrapper;
